import ExcelJS from 'exceljs';
import type { WesApiExport } from './wesPalletStagingApi';
import { toDetailRow, type WesDetailRowView } from './wesPalletStagingMappers';
import { WES_DIMENSIONS, WES_METRICS, type WesDimKey, type WesDisplayMode, type WesMetricKey } from './wesPalletStagingTable';

const pad = (n: number) => String(n).padStart(2, '0');

export const exportFileName = (date: Date = new Date()) =>
  `WESPalletStagingAnalytics_LocationDetail_${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}.xlsx`;

// Total Pallets is always a count; staged columns follow the display mode, with - where the value is unavailable.
const metricCell = (row: WesDetailRowView, key: WesMetricKey, mode: WesDisplayMode): { value: number | string; numFmt?: string } => {
  if (key === 'total' || mode === 'pallets') {
    const count = row.counts[key];
    return { value: count === null ? '-' : count, numFmt: '#,##0' };
  }
  const pct = row.pcts[key];
  return pct === null ? { value: '-' } : { value: pct / 100, numFmt: '0.0%' };
};

// Header, then every row in API order; no Total row.
export const buildWesWorkbook = (data: WesApiExport, dimensions: WesDimKey[], mode: WesDisplayMode): ExcelJS.Workbook => {
  const dims = WES_DIMENSIONS.filter((d) => dimensions.includes(d.key));
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Location Detail');

  const header = sheet.addRow([...dims.map((d) => d.label), ...WES_METRICS.map((m) => m.label)]);
  header.font = { bold: true };

  const addRow = (row: WesDetailRowView) => {
    const added = sheet.addRow([...dims.map((d) => row.text[d.key] ?? ''), ...WES_METRICS.map(() => '')]);
    WES_METRICS.forEach((m, i) => {
      const cell = added.getCell(dims.length + i + 1);
      const { value, numFmt } = metricCell(row, m.key, mode);
      cell.value = value;
      if (numFmt) cell.numFmt = numFmt;
      cell.alignment = { horizontal: 'right' };
    });
    return added;
  };

  data.rows.forEach((row, index) => addRow(toDetailRow(row, index, mode)));

  sheet.columns.forEach((column, i) => {
    const label = i < dims.length ? dims[i].label : WES_METRICS[i - dims.length].label;
    column.width = Math.max(12, label.length + 2);
  });
  return workbook;
};
